import { chartCategorical } from "@opus-dt/ui-tokens";
import { formatQuantity } from "@/lib/quantity-format";
import type { ReportStage } from "@/types/production-report.types";

// Spec 083 (T034) — a saída por destino de uma etapa. O rótulo é "destino", e não "produto", porque
// o item cadastrado costuma ser para onde a saída vai (embalagem, granel, setor interno), não um produto
// distinto — há fábricas que fazem um produto só e o repartem entre destinos.
//
// ⚠️ Produto SEM registro no período não aparece. Ausência não é zero: uma linha zerada afirmaria
// que a fábrica não produziu nada daquele produto, quando o que houve foi não ter havido medição.
// A ordem é por volume, que é a pergunta que o gestor faz primeiro ("o que mais saiu").
//
// A barra empilhada responde a pergunta do mix numa olhada; a lista responde à da conferência. Uma
// barra POR destino (o desenho anterior) respondia a terceira pergunta, que ninguém faz — quanto
// cada destino representa dele mesmo —, e ainda gastava oito trilhos para dizer o que um diz.
//
// ⚠️ A paleta categórica tem 7 cores: com 8 destinos ou mais, a 8ª repete a 1ª. O nome ao lado do
// quadrado é que identifica; a cor é reforço, nunca a chave.

interface Props {
  stage: ReportStage;
}

const pct = new Intl.NumberFormat("pt-BR", { maximumFractionDigits: 1 });

export function ProductBreakdown({ stage }: Props) {
  const total = stage.products.reduce((acc, p) => acc + p.quantity, 0);
  const unidade = stage.unitIsEquivalent ? `${stage.unit}-eq` : stage.unit;

  if (stage.products.length === 0) {
    return (
      <p className="text-[12px] text-text-secondary">
        Nenhuma saída com registro no período nesta linha.
      </p>
    );
  }

  const fatias = stage.products.map((p, i) => ({
    ...p,
    share: total > 0 ? (p.quantity / total) * 100 : 0,
    cor: chartCategorical(i),
  }));

  return (
    <div className="space-y-3">
      <div className="flex items-baseline justify-between">
        <h3 className="text-[13px] font-medium text-text-primary">Saída por destino</h3>
        <span className="text-[11px] text-text-tertiary">
          {formatQuantity(total)} {unidade} no período
        </span>
      </div>

      <div
        role="img"
        aria-label={`Mix da saída: ${fatias
          .map((f) => `${f.productName} ${pct.format(f.share)}%`)
          .join(", ")}`}
        className="flex h-3.5 gap-0.5 overflow-hidden rounded-full"
      >
        {fatias.map((f) => (
          <span
            key={f.productId}
            className="w-[var(--seg-w)] bg-[var(--seg-c)]"
            style={{ "--seg-w": `${f.share}%`, "--seg-c": f.cor } as React.CSSProperties}
          />
        ))}
      </div>

      <ul className="space-y-2">
        {fatias.map((f) => (
          <li key={f.productId} className="flex items-center gap-2.5">
            <span
              aria-hidden
              className="h-2 w-2 shrink-0 rounded-[2px] bg-[var(--seg-c)]"
              style={{ "--seg-c": f.cor } as React.CSSProperties}
            />
            <span className="min-w-0 flex-1 truncate text-[13px] text-text-primary">
              {f.productName}
            </span>
            <span className="font-mono text-[13px] font-medium tabular-nums text-text-primary">
              {formatQuantity(f.quantity)}
            </span>
            <span className="w-11 text-right font-mono text-[11px] tabular-nums text-text-tertiary">
              {pct.format(f.share)}%
            </span>
          </li>
        ))}
      </ul>

      {stage.unitIsEquivalent ? (
        <p className="text-[11px] text-text-tertiary">
          Produto contado em embalagem entra convertido pela gramatura antes de somar — por isso a
          unidade aparece como {unidade}.
        </p>
      ) : null}
    </div>
  );
}
